/**
 * 이미지 업로드 유틸리티 (multipart/form-data 전용)
 * - 서버에 임시 이미지 업로드 (TTL 24시간)
 * - ⭐ 모든 이미지 multipart로 전송 (base64 제거)
 * - ⭐ 서버 측 리사이징 (500KB 이상 → 1024px)
 */

import { debugLog, debugWarn, debugError } from './debug.js';

// Stub functions for sessionStorage (simplified for learning project)
const safeSetSessionStorage = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('sessionStorage set failed:', e);
    return false;
  }
};

const safeRemoveSessionStorage = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    console.warn('sessionStorage remove failed:', e);
  }
};

// Stub function for GA (not used in learning project)
const sendGAEvent = () => {};
/**
 * 서버에서 리사이징 설정 가져오기 (캐싱 포함)
 */
let resizeConfigCache = null;
let configCacheTime = 0;
const CONFIG_CACHE_TTL = 60000; // 1분

async function getResizeConfig() {
  const now = Date.now();

  // 캐시된 설정이 유효하면 반환
  if (resizeConfigCache && (now - configCacheTime < CONFIG_CACHE_TTL)) {
    return resizeConfigCache;
  }

  try {
    debugLog('[ImageUpload] 🔄 Fetching resize config from server...');
    const response = await fetch('/api/config/resize');
    const data = await response.json();

    if (data.success && data.config) {
      resizeConfigCache = data.config;
      configCacheTime = now;
      debugLog('[ImageUpload] ✅ Resize config loaded:', resizeConfigCache);
      return resizeConfigCache;
    }
  } catch (error) {
    debugWarn('[ImageUpload] ⚠️ Failed to fetch config, using defaults:', error.message);
  }

  // 기본값 반환
  const defaultConfig = {
    maxDimension: 2048,
    quality: 85,
    clientResizeEnabled: true,
    serverResizeEnabled: true,
    clientTimeout: 15000,
  };

  resizeConfigCache = defaultConfig;
  configCacheTime = now;
  return defaultConfig;
}

/**
 * 클라이언트 측 이미지 리사이징 (Canvas 사용)
 *
 * ⚠️ 메모리 최적화:
 * - 저사양 모바일 기기를 위한 점진적 리사이징
 * - Canvas 메모리 제한 대응 (Safari: ~384MB, Android: 다양)
 * - 대용량 이미지는 다단계로 축소하여 메모리 사용량 감소
 *
 * @param {File|Blob} file - 리사이징할 파일
 * @param {number} maxDimension - 최대 차원 (기본: 2048px)
 * @param {number} quality - JPEG 품질 (기본: 0.85)
 * @returns {Promise<Blob>} - 리사이징된 이미지 Blob
 */
async function resizeImageBeforeUpload(file, maxDimension = 2048, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      try {
        const originalWidth = img.width;
        const originalHeight = img.height;
        const maxOriginalDimension = Math.max(originalWidth, originalHeight);

        // 이미 작으면 원본 반환
        if (maxOriginalDimension <= maxDimension) {
          debugLog('[ImageUpload] 📏 Image already within size limit, no resize needed');
          resolve(file);
          return;
        }

        // 비율 유지하며 최종 크기 계산
        let finalWidth, finalHeight;
        if (originalWidth > originalHeight) {
          finalWidth = maxDimension;
          finalHeight = Math.round((originalHeight / originalWidth) * maxDimension);
        } else {
          finalHeight = maxDimension;
          finalWidth = Math.round((originalWidth / originalHeight) * maxDimension);
        }

        debugLog('[ImageUpload] 📏 Resizing image:', {
          from: `${originalWidth}x${originalHeight}`,
          to: `${finalWidth}x${finalHeight}`,
          method: maxOriginalDimension > maxDimension * 2 ? 'progressive' : 'direct',
        });

        // ⭐ 간단하고 안전한 리사이징
        // 점진적 리사이징은 메모리 효율적이지만 복잡도가 높아 에러 가능성 증가
        // 대신 한 번에 처리하되 메모리 해제를 확실히 수행
        const canvas = document.createElement('canvas');
        canvas.width = finalWidth;
        canvas.height = finalHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // 고품질 리사이징 옵션 설정
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // 이미지 그리기
        ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

        // Blob으로 변환
        canvas.toBlob(
          (blob) => {
            // Canvas 메모리 즉시 해제 (중요!)
            canvas.width = 0;
            canvas.height = 0;

            if (blob) {
              debugLog('[ImageUpload] ✅ Resize complete:', {
                originalSize: (file.size / 1024).toFixed(2) + 'KB',
                resizedSize: (blob.size / 1024).toFixed(2) + 'KB',
                reduction: `${((1 - blob.size / file.size) * 100).toFixed(1)}%`,
              });
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        debugError('[ImageUpload] ❌ Resize error:', error);
        // 리사이징 실패 시 원본 반환 (fallback)
        debugWarn('[ImageUpload] Falling back to original image due to resize error');
        resolve(file);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // 이미지 로드 실패 시 원본 반환 (fallback)
      debugError('[ImageUpload] Failed to load image, using original');
      resolve(file);
    };

    img.src = url;
  });
}

/**
 * ⭐ 간소화된 이미지 업로드 (multipart 전용)
 *
 * @param {File|Blob} file - 업로드할 이미지 파일
 * @param {Object} options - 옵션
 * @param {number} options.ttl - TTL (초, 기본: 86400 = 24시간)
 * @param {boolean} options.saveToSession - sessionStorage에 URL 저장 여부 (기본: true)
 * @param {boolean} options.resize - 업로드 전 클라이언트 리사이징 여부 (기본: null, 서버 설정 사용)
 * @param {number} options.maxDimension - 최대 차원 (기본: null, 서버 설정 사용)
 * @returns {Promise<string>} - 업로드된 이미지 URL
 */
export async function uploadImageToServer(file, options = {}) {
  // ⭐ 서버에서 설정 가져오기
  const serverConfig = await getResizeConfig();

  // ⭐ 하이브리드 방식: 클라이언트 먼저 시도 → 실패 시 서버 백업
  const {
    ttl = 86400,
    saveToSession = true,
    resize = serverConfig.clientResizeEnabled,
    maxDimension = serverConfig.maxDimension
  } = options;

  try {
    debugLog('[ImageUpload] 📤 Starting multipart upload...', {
      size: file ? (file.size / 1024).toFixed(2) + 'KB' : 'unknown',
      ttl,
      saveToSession,
      resize,
    });

    if (!file || !(file instanceof File || file instanceof Blob)) {
      throw new Error('Invalid image source: must be File or Blob');
    }

    // ⭐ 클라이언트 측 리사이징 (옵션)
    let fileToUpload = file;
    let clientResizeFailed = false;

    if (resize) {
      // 파일 크기 사전 체크 (10MB 이상은 경고)
      const fileSizeMB = file.size / 1024 / 1024;
      if (fileSizeMB > 10) {
        debugWarn('[ImageUpload] ⚠️ Large file detected:', fileSizeMB.toFixed(2) + 'MB');
      }

      try {
        // 타임아웃 설정 (서버 설정 사용)
        const quality = serverConfig.quality / 100; // 1-100 → 0-1
        const resizePromise = resizeImageBeforeUpload(file, maxDimension, quality);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Resize timeout')), serverConfig.clientTimeout)
        );

        fileToUpload = await Promise.race([resizePromise, timeoutPromise]);
        debugLog('[ImageUpload] ✅ Client resize successful');

        // 🔔 Google Analytics: 클라이언트 리사이징 성공
        try {
          sendGAEvent('image_resize_client_success', {
            original_size_mb: (file.size / 1024 / 1024).toFixed(2),
            resized_size_mb: (fileToUpload.size / 1024 / 1024).toFixed(2),
            reduction_percent: ((1 - fileToUpload.size / file.size) * 100).toFixed(1),
            max_dimension: maxDimension,
          });
        } catch (gaError) {
          debugWarn('[ImageUpload] Failed to send GA event:', gaError);
        }

      } catch (resizeError) {
        clientResizeFailed = true;
        const errorType = resizeError.message.includes('timeout') ? 'timeout' :
                          resizeError.message.includes('memory') ? 'memory' : 'unknown';

        debugWarn('[ImageUpload] ⚠️ Client resize failed:', resizeError.message);
        debugLog('[ImageUpload] 📤 Uploading original file to server');

        // 🔔 Google Analytics: 클라이언트 리사이징 실패 이벤트
        try {
          sendGAEvent('image_resize_client_failed', {
            error_type: errorType,
            error_message: resizeError.message,
            file_size_mb: (file.size / 1024 / 1024).toFixed(2),
            file_type: file.type || 'unknown',
            max_dimension: maxDimension,
          });
        } catch (gaError) {
          debugWarn('[ImageUpload] Failed to send GA event:', gaError);
        }

        fileToUpload = file;
      }
    }

    // ⭐ FormData 생성 (multipart)
    const formData = new FormData();
    formData.append('image', fileToUpload);
    formData.append('ttl', ttl.toString());

    // ⭐ multipart POST
    const response = await fetch('/api/image/upload', {
      method: 'POST',
      body: formData,  // No Content-Type header needed for FormData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    if (!data.success || !data.imageUrl) {
      throw new Error('Invalid upload response: ' + JSON.stringify(data));
    }

    debugLog('[ImageUpload] ✅ Upload successful:', {
      imageUrl: data.imageUrl,
      imageId: data.imageId,
      size: (data.size / 1024).toFixed(2) + 'KB',
      resized: data.resized,  // ⭐ 서버 리사이징 여부
      expiresIn: `${data.expiresIn}s`,
    });

    // 🔔 Google Analytics: 서버 백업 리사이징 추적
    if (clientResizeFailed && data.resized && data.resizeMethod === 'photon-wasm') {
      try {
        sendGAEvent('image_resize_server_backup_success', {
          original_size_mb: (file.size / 1024 / 1024).toFixed(2),
          final_size_kb: (data.size / 1024).toFixed(2),
          resize_method: data.resizeMethod,
        });
      } catch (gaError) {
        debugWarn('[ImageUpload] Failed to send GA event:', gaError);
      }
    }

    // sessionStorage에 URL 저장
    if (saveToSession) {
      safeSetSessionStorage('temp_image_url', data.imageUrl);
      safeSetSessionStorage('temp_image_id', data.imageId);

      // 만료 시간도 저장
      const expiresAt = Date.now() + (data.expiresIn || ttl) * 1000;
      safeSetSessionStorage('temp_image_expires_at', expiresAt.toString());
    }

    return data.imageUrl;
  } catch (error) {
    debugError('[ImageUpload] ❌ Upload failed:', error);
    throw error;
  }
}

/**
 * ⚠️ DEPRECATED: 클라이언트 리사이징 (서버 측으로 이동됨)
 *
 * 이미지를 리사이징 (썸네일 크기로 축소)
 * @deprecated Use server-side resizing via uploadImageToServer() instead
 * @param {File|Blob} file - 리사이징할 이미지 파일
 * @param {number} maxWidth - 최대 너비 (기본: 800px)
 * @param {number} maxHeight - 최대 높이 (기본: 800px)
 * @param {number} quality - JPEG 품질 (0-1, 기본: 0.85)
 * @returns {Promise<string>} - 리사이징된 이미지의 base64 문자열
 */
export async function resizeImage(file, maxWidth = 800, maxHeight = 800, quality = 0.85) {
  debugWarn('[ImageUpload] ⚠️ resizeImage() is deprecated. Use server-side resizing instead.');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // 원본 크기
        const originalWidth = img.width;
        const originalHeight = img.height;

        // 리사이징 필요 여부 확인
        if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
          debugLog('[ImageUpload] 📏 Image already small enough, no resize needed:', {
            width: originalWidth,
            height: originalHeight
          });
          // 리사이징 불필요 - 원본 반환
          resolve(e.target.result);
          return;
        }

        // 비율 유지하며 축소
        let newWidth = originalWidth;
        let newHeight = originalHeight;

        if (originalWidth > maxWidth) {
          newWidth = maxWidth;
          newHeight = (originalHeight * maxWidth) / originalWidth;
        }

        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          newWidth = (originalWidth * maxHeight) / originalHeight;
        }

        debugLog('[ImageUpload] 📏 Resizing image:', {
          from: `${originalWidth}x${originalHeight}`,
          to: `${Math.round(newWidth)}x${Math.round(newHeight)}`,
          reduction: `${((1 - (newWidth * newHeight) / (originalWidth * originalHeight)) * 100).toFixed(1)}%`
        });

        // Canvas에 리사이징
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // base64로 변환
        const resizedBase64 = canvas.toDataURL('image/jpeg', quality);

        debugLog('[ImageUpload] ✅ Resize complete:', {
          originalSize: `${(e.target.result.length / 1024).toFixed(2)} KB`,
          resizedSize: `${(resizedBase64.length / 1024).toFixed(2)} KB`,
          reduction: `${((1 - resizedBase64.length / e.target.result.length) * 100).toFixed(1)}%`
        });

        resolve(resizedBase64);
      };

      img.onerror = () => reject(new Error('Failed to load image for resizing'));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error('Failed to read file for resizing'));
    reader.readAsDataURL(file);
  });
}

/**
 * ⚠️ DEPRECATED: base64 변환 (multipart로 대체됨)
 *
 * File 객체를 base64 문자열로 변환 (리사이징 포함)
 * @deprecated Use multipart/form-data upload instead
 * @param {File|Blob} file - 변환할 파일
 * @param {Object} options - 옵션
 * @param {boolean} options.resize - 리사이징 여부 (기본: true)
 * @param {number} options.maxWidth - 최대 너비 (기본: 800px)
 * @param {number} options.maxHeight - 최대 높이 (기본: 800px)
 * @param {number} options.quality - JPEG 품질 (기본: 0.85)
 * @returns {Promise<string>} - base64 문자열 (data:image/...;base64,...)
 */
export async function fileToBase64(file, options = {}) {
  debugWarn('[ImageUpload] ⚠️ fileToBase64() is deprecated. Use multipart upload instead.');
  const {
    resize = true,
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.85
  } = options;

  // 리사이징이 활성화된 경우
  if (resize) {
    return resizeImage(file, maxWidth, maxHeight, quality);
  }

  // 리사이징 비활성화 - 원본 반환
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * 이미지가 서버에 존재하는지 확인 (HEAD 요청)
 * @param {string} imageUrl - 확인할 이미지 URL
 * @returns {Promise<boolean>} - 존재 여부
 */
export async function checkImageExists(imageUrl) {
  if (!imageUrl) {
    debugWarn('[ImageUpload] No URL provided for existence check');
    return false;
  }

  try {
    debugLog('[ImageUpload] 🔍 Checking image existence:', imageUrl);

    const response = await fetch(imageUrl, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit'
    });

    const exists = response.ok;
    debugLog(`[ImageUpload] ${exists ? '✅' : '❌'} Image ${exists ? 'exists' : 'not found'}`);

    return exists;
  } catch (error) {
    debugError('[ImageUpload] ❌ Check failed:', error);
    // CORS 에러가 발생해도 이미지가 존재한다고 가정 (실제로는 존재함)
    debugLog('[ImageUpload] ⚠️ CORS error, assuming image exists');
    return true;
  }
}

/**
 * sessionStorage에 저장된 임시 이미지 URL 가져오기 + 만료 검증
 * @returns {Promise<string|null>} - 유효한 이미지 URL 또는 null
 */
export async function getTempImageUrl() {
  const imageUrl = sessionStorage.getItem('temp_image_url');
  if (!imageUrl) {
    debugLog('[ImageUpload] No temp image URL in sessionStorage');
    return null;
  }

  // 만료 시간 확인
  const expiresAt = sessionStorage.getItem('temp_image_expires_at');
  if (expiresAt) {
    const now = Date.now();
    const expires = parseInt(expiresAt);

    if (now >= expires) {
      debugWarn('[ImageUpload] ⚠️ Temp image URL expired');
      safeRemoveSessionStorage('temp_image_url');
      safeRemoveSessionStorage('temp_image_id');
      safeRemoveSessionStorage('temp_image_expires_at');
      return null;
    }

    const remainingSeconds = Math.floor((expires - now) / 1000);
    debugLog(`[ImageUpload] ⏱️ Temp image expires in ${remainingSeconds}s`);
  }

  // 서버에 실제 존재하는지 확인
  const exists = await checkImageExists(imageUrl);

  if (!exists) {
    debugWarn('[ImageUpload] ⚠️ Temp image not found on server');
    safeRemoveSessionStorage('temp_image_url');
    safeRemoveSessionStorage('temp_image_id');
    safeRemoveSessionStorage('temp_image_expires_at');
    return null;
  }

  debugLog('[ImageUpload] ✅ Valid temp image URL:', imageUrl);
  return imageUrl;
}

/**
 * sessionStorage에서 임시 이미지 데이터 모두 삭제
 */
export function clearTempImageData() {
  debugLog('[ImageUpload] 🗑️ Clearing temp image data from sessionStorage');
  safeRemoveSessionStorage('temp_image_url');
  safeRemoveSessionStorage('temp_image_id');
  safeRemoveSessionStorage('temp_image_expires_at');
  safeRemoveSessionStorage('originalImagePreview');  // 기존 base64도 삭제
}

/**
 * 하이브리드 이미지 저장 전략
 * 1차: 서버 업로드 시도
 * 2차: sessionStorage에 base64 저장 (서버 실패 시)
 * @param {File|Blob} file - 저장할 이미지 파일
 * @param {string} preview - 로컬 미리보기 URL (blob: 또는 data:)
 * @returns {Promise<Object>} - { serverUrl, localPreview, storageType }
 */
export async function saveImageWithFallback(file, preview) {
  let serverUrl = null;
  let storageType = null;

  // 1차: 서버 업로드 시도
  try {
    serverUrl = await uploadImageToServer(file, { ttl: 86400 });
    storageType = 'server';
    debugLog('[ImageUpload] ✅ Primary: Server upload successful');
  } catch (uploadError) {
    debugWarn('[ImageUpload] ⚠️ Primary: Server upload failed, trying fallback...', uploadError);

    // 2차: sessionStorage에 base64 저장 시도
    try {
      const base64 = preview.startsWith('data:') ? preview : await fileToBase64(file);
      const success = safeSetSessionStorage('originalImagePreview', base64);

      if (success) {
        storageType = 'sessionStorage';
        debugLog('[ImageUpload] ✅ Fallback: Stored in sessionStorage');
      } else {
        storageType = 'local-only';
        debugWarn('[ImageUpload] ⚠️ Fallback: sessionStorage failed, using local preview only');
      }
    } catch (storageError) {
      storageType = 'local-only';
      debugError('[ImageUpload] ❌ Fallback: Both server and sessionStorage failed');
    }
  }

  return {
    serverUrl: serverUrl || null,
    localPreview: preview,
    storageType: storageType,
    canRestore: storageType !== 'local-only',  // 뒤로가기 시 복원 가능 여부
  };
}

/**
 * R2 직접 업로드 (Workers Proxy 사용)
 * - 클라이언트 리사이징 후 R2로 업로드
 * - Workers가 R2에 스트리밍 전송 (메모리 효율적)
 * - TTL 24시간 자동 설정
 * 
 * @param {File|Blob} file - 업로드할 파일
 * @param {Object} options - 업로드 옵션
 * @param {boolean} options.resize - 클라이언트 리사이징 여부 (기본: true)
 * @param {number} options.maxDimension - 최대 크기 (기본: 2048px)
 * @returns {Promise<string>} R2 Public URL
 */
export async function uploadImageToR2(file, options = {}) {
  const {
    resize = true,
    maxDimension = 2048,
  } = options;

  try {
    debugLog('[R2 Upload] 📤 Starting R2 upload...', {
      size: file ? (file.size / 1024).toFixed(2) + 'KB' : 'unknown',
      resize,
    });

    if (!file || !(file instanceof File || file instanceof Blob)) {
      throw new Error('Invalid image source: must be File or Blob');
    }

    // ⭐ 클라이언트 측 리사이징 (조건부)
    let fileToUpload = file;
    const fileSizeMB = file.size / 1024 / 1024;

    if (resize && fileSizeMB < 10) {
      // 10MB 미만 파일만 클라이언트 리사이징 시도
      try {
        debugLog('[R2 Upload] 🔄 Attempting client resize...');
        const resizePromise = resizeImageBeforeUpload(file, maxDimension, 0.85);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Resize timeout')), 15000)
        );

        fileToUpload = await Promise.race([resizePromise, timeoutPromise]);
        debugLog('[R2 Upload] ✅ Client resize successful:', {
          from: (file.size / 1024).toFixed(2) + 'KB',
          to: (fileToUpload.size / 1024).toFixed(2) + 'KB',
        });

        // Google Analytics
        try {
          sendGAEvent('image_resize_r2_success', {
            original_size_mb: (file.size / 1024 / 1024).toFixed(2),
            resized_size_mb: (fileToUpload.size / 1024 / 1024).toFixed(2),
          });
        } catch (gaError) {
          debugWarn('[R2 Upload] GA event failed:', gaError);
        }

      } catch (resizeError) {
        debugWarn('[R2 Upload] ⚠️ Client resize failed, using original:', resizeError.message);
        fileToUpload = file;

        // Google Analytics
        try {
          sendGAEvent('image_resize_r2_failed', {
            error_type: resizeError.message.includes('timeout') ? 'timeout' : 'unknown',
            file_size_mb: (file.size / 1024 / 1024).toFixed(2),
          });
        } catch (gaError) {
          debugWarn('[R2 Upload] GA event failed:', gaError);
        }
      }
    } else if (fileSizeMB >= 10) {
      debugLog('[R2 Upload] ℹ️ Large file, skipping client resize');
    }

    // ⭐ FormData 생성
    const formData = new FormData();
    formData.append('image', fileToUpload);

    // ⭐ R2 업로드 (Workers Proxy)
    debugLog('[R2 Upload] 📤 Uploading to R2 via Workers...');
    const response = await fetch('/api/image/upload-r2', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`R2 upload failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'R2 upload failed');
    }

    debugLog('[R2 Upload] ✅ Upload successful:', data.imageUrl);

    // Google Analytics
    try {
      sendGAEvent('image_upload_r2_success', {
        size_mb: (fileToUpload.size / 1024 / 1024).toFixed(2),
        image_id: data.imageId,
      });
    } catch (gaError) {
      debugWarn('[R2 Upload] GA event failed:', gaError);
    }

    return data.imageUrl;

  } catch (error) {
    debugError('[R2 Upload] ❌ Error:', error);

    // Google Analytics
    try {
      sendGAEvent('image_upload_r2_error', {
        error_message: error.message,
        file_size_mb: file ? (file.size / 1024 / 1024).toFixed(2) : 'unknown',
      });
    } catch (gaError) {
      debugWarn('[R2 Upload] GA event failed:', gaError);
    }

    throw error;
  }
}
